import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw !== confirm) { setErr('Passwords do not match'); return; }
    if (pw.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Reset link is invalid or expired.');
    }
    setBusy(false);
  };

  return (
    <div className="app-container min-h-screen bg-[#F5F5F0]" data-testid="reset-password-page">
      <div className="px-6 pt-16 pb-8">
        <h1 className="text-3xl font-black tracking-tight text-[#111111]">Choose a new password</h1>
        <p className="text-sm text-[#6B6E73] mt-2 leading-relaxed">
          Make it at least 6 characters. Try not to reuse one from another account.
        </p>

        {!token && (
          <p data-testid="reset-no-token" className="mt-6 bg-red-50 border border-red-100 rounded-2xl p-4 text-red-600 text-sm">
            This link is missing or invalid. Request a new one from <Link to="/forgot-password" className="underline font-bold">Forgot password</Link>.
          </p>
        )}

        {done ? (
          <div className="mt-8 bg-white border border-[#B6E600]/30 rounded-2xl p-5" data-testid="reset-done">
            <CheckCircle2 size={28} className="text-[#3F5200] mb-3" />
            <p className="text-sm font-bold text-[#111111]">Password updated</p>
            <p className="text-xs text-[#6B6E73] mt-1">You can now sign in with your new password.</p>
            <button
              data-testid="reset-go-login"
              onClick={() => navigate('/login')}
              className="mt-5 w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-3 text-sm active:scale-[0.97]"
            >
              Go to sign in
            </button>
          </div>
        ) : token ? (
          <form onSubmit={submit} className="mt-8 space-y-3.5">
            {err && (
              <div data-testid="reset-error" className="bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-red-600 text-sm">{err}</div>
            )}
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B0B3B8]" />
              <input
                data-testid="reset-password-input"
                type="password"
                value={pw}
                onChange={e => setPw(e.target.value)}
                placeholder="New password"
                required
                className="w-full bg-white text-[#111111] rounded-2xl pl-11 pr-4 py-[14px] text-sm border border-[#E8E8E8] focus:outline-none focus:border-[#B6E600] focus:shadow-[0_0_0_3px_rgba(182,230,0,0.12)] transition-all"
              />
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B0B3B8]" />
              <input
                data-testid="reset-password-confirm"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                required
                className="w-full bg-white text-[#111111] rounded-2xl pl-11 pr-4 py-[14px] text-sm border border-[#E8E8E8] focus:outline-none focus:border-[#B6E600] focus:shadow-[0_0_0_3px_rgba(182,230,0,0.12)] transition-all"
              />
            </div>
            <button
              data-testid="reset-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-[14px] text-sm flex items-center justify-center gap-2 active:scale-[0.97] disabled:opacity-60 shadow-[0_2px_12px_rgba(182,230,0,0.25)]"
            >
              {busy ? <><Loader2 size={18} className="animate-spin" /> Updating…</> : 'Update password'}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
